/**
 * LinkContentSettingItem
 *
 * Single-row setting for the Link Content option in the publish flow.
 * Mirrors Flutter's `_linkContentWidget` from content_settings_screen.dart:
 *  - Icon + label + trailing square, with description below.
 *  - When unlinked: label = "Link to your other videos", trailing = AddMediaButton (square).
 *  - When linked:   label = "Unlink video",                trailing = thumbnail of linked media.
 *  - Tapping the label toggles link/unlink. Tapping the trailing add button links.
 */

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AddMediaButton } from '@/presentation/components/common/controls/AddMediaButton';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

interface LinkedMediaThumbnailProps {
  media: UserMediaEntity;
  size: number;
  borderRadius: number;
  backgroundColor: string;
}

const LinkedMediaThumbnail = ({ media, size, borderRadius, backgroundColor }: LinkedMediaThumbnailProps) => {
  const thumbnailUrl = MediaUtils.getMediaThumbnailUrl(media);
  return (
    <div
      onClick={() => Navigator.navigateToContentDetails(media.id, { medias: [media] })}
      style={{
        width: size,
        height: size,
        borderRadius,
        overflow: 'hidden',
        backgroundColor,
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      {thumbnailUrl && (
        <CachedImage
          src={thumbnailUrl}
          alt={media.title || media.description || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
    </div>
  );
};

interface LinkContentSettingItemProps {
  iconPath: string;
  description: string;
  linkedMedia: UserMediaEntity | null;
  onLinkClick: () => void;
  onUnlinkClick: () => void;
}

export const LinkContentSettingItem = ({
  iconPath,
  description,
  linkedMedia,
  onLinkClick,
  onUnlinkClick,
}: LinkContentSettingItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const isLinked = linkedMedia !== null;
  const label = isLinked ? l10n.publish.linkContentUnlink : l10n.publish.linkContentLink;
  const handleLabelClick = isLinked ? onUnlinkClick : onLinkClick;

  const squareSize = responsive.avatar.large;
  const squareRadius = responsive.borderRadius.base;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.spacing.xs,
        }}
      >
        <AssetIcon
          path={iconPath}
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          {...qaAttr({ id: 'content-setting-link', value: isLinked ? 'linked' : 'unlinked' })}
          onClick={handleLabelClick}
          style={{
            flex: 1,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
            cursor: 'pointer',
          }}
        >
          {label}
        </span>
        {linkedMedia ? (
          <LinkedMediaThumbnail
            media={linkedMedia}
            size={squareSize}
            borderRadius={squareRadius}
            backgroundColor={theme.surface}
          />
        ) : (
          <AddMediaButton
            buttonSize={squareSize}
            iconSize={responsive.icon.medium}
            backgroundColor="transparent"
            iconBackgroundColor={theme.secondary}
            borderRadius={squareRadius}
            onTap={onLinkClick}
          />
        )}
      </div>
      <div
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
          fontWeight: responsive.fontWeight.medium,
          marginTop: responsive.spacing.sm,
        }}
      >
        {description}
      </div>
    </div>
  );
};
